import {
  AccessTime,
  Add,
  Block,
  Business,
  CheckCircle,
  Close,
  ContentCopy,
  CreditCard,
  DeleteForever,
  Edit,
  Key,
  Lock,
  LockOpen,
  LockReset,
  MenuBook,
  People,
  Person,
  Save,
  ShoppingBag,
  WhatsApp,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import {
  dashboardCardSx,
  dashboardPagePaddingSx,
  dashboardPageSx,
  dashboardSectionTitleSx,
} from "../components/layout/dashboardStyles";
import { PageLoadingState } from "../components/layout/PageLoadingState";
import { FeedbackSnackbar } from "../components/design/FeedbackSnackbar";
import { SectionTabs } from "../components/design/SectionTabs";
import {
  AgentWorkflow,
  COMPANY_TYPE_CAPABILITIES,
  COMPANY_TYPE_OPTIONS,
  CompanyType,
  WORKFLOW_LABELS,
} from "../config/companyCapabilities";
import { IntegrationCodeEnum } from "../config/access";
import { adminApi } from "../data/api/AdminApi";
import {
  AdminBillingPlan,
  AdminBillingPlanInput,
  AdminCompany,
  AdminCompanyBillingInput,
  AdminCompanyInput,
  AdminPaymentGateway,
  AdminUser,
  AdminUserInput,
  AdminUserUpdateInput,
} from "../data/models/Admin/AdminModels";
import { ApiResponse } from "../utils/RequestBuilder";
import { getConfiguredApiBaseUrl } from "../utils/RequestBuilder";
import { TravelBookingRequest, TravelBookingStatus } from "../data/models/Experience/ExperienceModels";

type ScheduleValue =
  | { mode: "fixed"; fixed: { opening: string; closing: string } }
  | { mode: "variable"; days: Record<string, { opening: string; closing: string } | null> };

const DAYS = ["lunes", "martes", "miércoles", "jueves", "viernes", "sábado", "domingo"];
const SENSITIVE_FIELDS: (keyof AdminCompanyInput)[] = [
  "accessToken",
  "iv",
  "encryptedData",
];

const COUNTRY_OPTIONS = [
  { code: "CO", name: "Colombia", currency: "COP" },
  { code: "US", name: "Estados Unidos", currency: "USD" },
  { code: "MX", name: "Mexico", currency: "MXN" },
  { code: "PE", name: "Peru", currency: "PEN" },
  { code: "EC", name: "Ecuador", currency: "USD" },
  { code: "CL", name: "Chile", currency: "CLP" },
  { code: "AR", name: "Argentina", currency: "ARS" },
  { code: "BR", name: "Brasil", currency: "BRL" },
  { code: "PA", name: "Panama", currency: "PAB" },
  { code: "CR", name: "Costa Rica", currency: "CRC" },
  { code: "DO", name: "República Dominicana", currency: "DOP" },
  { code: "GT", name: "Guatemala", currency: "GTQ" },
  { code: "HN", name: "Honduras", currency: "HNL" },
  { code: "SV", name: "El Salvador", currency: "USD" },
  { code: "UY", name: "Uruguay", currency: "UYU" },
  { code: "PY", name: "Paraguay", currency: "PYG" },
  { code: "BO", name: "Bolivia", currency: "BOB" },
  { code: "VE", name: "Venezuela", currency: "USD" },
  { code: "CA", name: "Canada", currency: "CAD" },
  { code: "ES", name: "España", currency: "EUR" },
];

const CURRENCY_OPTIONS = [
  "COP",
  "USD",
  "EUR",
  "MXN",
  "PEN",
  "CLP",
  "ARS",
  "BRL",
  "CAD",
  "CRC",
  "DOP",
  "GTQ",
  "HNL",
  "PAB",
  "PYG",
  "UYU",
  "BOB",
];

const BILLING_INTERVAL_OPTIONS = [
  { value: "monthly", label: "Mensual" },
  { value: "quarterly", label: "Trimestral" },
  { value: "semiannual", label: "Semestral" },
  { value: "annual", label: "Anual" },
] as const;

const BILLING_STATUS_OPTIONS = [
  { value: "trial", label: "Prueba" },
  { value: "active", label: "Activo" },
  { value: "past_due", label: "Pago vencido" },
  { value: "paused", label: "Pausado" },
  { value: "cancelled", label: "Cancelado" },
] as const;

const INTEGRATION_OPTIONS = Object.values(IntegrationCodeEnum);

const defaultBilling = (): AdminCompanyBillingInput => ({
  planId: "",
  planName: "",
  integrationCode: "",
  provider: "BOLD",
  currency: "COP",
  amount: 0,
  interval: "monthly",
  status: "trial",
  paymentStatus: "pending",
  boldPaymentLink: "",
  boldCustomerIdentifier: "",
  boldSubscriptionReference: "",
  payerName: "",
  payerEmail: "",
  payerPhone: "",
  payerDocument: "",
  billingName: "",
  billingEmail: "",
  billingPhone: "",
  billingDocument: "",
  billingAddress: "",
  billingCity: "",
  billingCountry: "",
  paymentMethods: [],
  paymentHistory: [],
  paymentMethodConfigured: false,
  lastPaymentAmount: 0,
  lastPaymentCurrency: "",
  lastPaymentReference: "",
  lastPaymentStatus: "pending",
  notes: "",
});

const createDefaultPlan = (): AdminBillingPlanInput => ({
  planId: "",
  name: "",
  integrationCode: IntegrationCodeEnum.LYPRO_TRAVEL,
  description: "",
  currency: "COP",
  amount: 0,
  interval: "monthly",
  features: [],
  isActive: true,
});

const createDefaultGateway = (): AdminPaymentGateway => ({
  provider: "BOLD",
  displayName: "Bold",
  isActive: false,
  publicKey: "",
  merchantId: "",
  apiKey: "",
  webhookSecret: "",
  webhookUrl: "/billing/webhooks/bold",
  apiKeyConfigured: false,
  webhookSecretConfigured: false,
  notes: "",
});

const defaultSchedule = (): ScheduleValue => ({
  mode: "fixed",
  fixed: { opening: "08:00", closing: "21:00" },
});

const normalizeSchedule = (schedule?: Record<string, unknown>): ScheduleValue => {
  if ((schedule as any)?.mode === "variable") {
    return {
      mode: "variable",
      days: DAYS.reduce(
        (acc, day) => ({
          ...acc,
          [day]: (schedule as any).days?.[day] ?? null,
        }),
        {} as Record<string, { opening: string; closing: string } | null>,
      ),
    };
  }
  if ((schedule as any)?.mode === "fixed") {
    return {
      mode: "fixed",
      fixed: {
        opening: (schedule as any).fixed?.opening || "08:00",
        closing: (schedule as any).fixed?.closing || "21:00",
      },
    };
  }
  return defaultSchedule();
};

const normalizeBilling = (billing?: Partial<AdminCompanyBillingInput>): AdminCompanyBillingInput => ({
  ...defaultBilling(),
  ...billing,
  provider: "BOLD",
  currency: String(billing?.currency || "COP").toUpperCase(),
  amount: Number(billing?.amount || 0),
});

const createDefaultCompany = (): AdminCompanyInput => ({
  companyType: "BASIC",
  allowedIntegrations: [],
  allowedWorkflows: ["NONE"],
  companyId: "",
  name: "",
  countryCode: "CO",
  countryName: "Colombia",
  defaultCurrency: "COP",
  billing: defaultBilling(),
  nameBot: "",
  authMapos: false,
  idMapos: "",
  description: "",
  synonyms: "",
  recommendations: "",
  urlMenu: "",
  urlsMenu: [],
  websiteUrl: "",
  regionNumber: 0,
  schedule: defaultSchedule(),
  isReserveEnabled: false,
  takeOrdersEnabled: true,
  websitePickupEnabled: true,
  websiteDeliveryEnabled: true,
  agentWorkflow: "NONE",
  accessToken: "",
  phoneNumberId: "",
  whatsappId: "",
  metaWebhookProxyUrl: "",
  iv: "",
  encryptedData: "",
  subCompany: false,
});

const createDefaultUser = (): AdminUserInput => ({
  fullName: "",
  email: "",
  temporaryPassword: "",
  company: "",
  role: "ADM",
  integrations: [],
});

const getAgentWorkflowLabel = (value?: string) =>
  WORKFLOW_LABELS[value as AgentWorkflow] || WORKFLOW_LABELS.NONE;

const withTimeout = <T,>(request: Promise<ApiResponse<T>>, label: string) =>
  new Promise<ApiResponse<T>>((resolve) => {
    const timeout = window.setTimeout(
      () =>
        resolve({
          data: undefined,
          isSuccess: false,
          messageError: `${label} tardó demasiado. Revisa la conexión con el API y Firebase.`,
        }),
      20000,
    );
    request
      .then((result) => resolve(result))
      .catch((error: Error) =>
        resolve({ data: undefined, isSuccess: false, messageError: error.message }),
      )
      .finally(() => window.clearTimeout(timeout));
  });

const toCompanyInput = (company: AdminCompany): AdminCompanyInput => {
  const companyType =
    company.companyType ||
    (company.idMapos ||
    company.agentWorkflow === "MAPOS_RESTAURANT"
      ? "MAPOS"
      : "BASIC");
  const capabilities = COMPANY_TYPE_CAPABILITIES[companyType];
  const allowedIntegrations = [...(company.allowedIntegrations || [])];
  let availableWorkflows =
    companyType === "BASIC" &&
    allowedIntegrations.includes(IntegrationCodeEnum.LYPRO_MAPOS_NOTIFICATIONS_BOT)
      ? [...capabilities.workflows, "EXTERNAL_BOT" as AgentWorkflow]
      : capabilities.workflows;
  if (allowedIntegrations.includes(IntegrationCodeEnum.LYPRO_TRAVEL)) availableWorkflows = [...availableWorkflows, "LYPRO_TRAVEL"];
  const configuredWorkflows = company.allowedWorkflows ||
    (companyType === "MAPOS" ? capabilities.workflows : ["NONE"]);
  const allowedWorkflows = configuredWorkflows.filter(
    (workflow) => availableWorkflows.includes(workflow),
  );
  if (
    companyType === "MAPOS" &&
    (configuredWorkflows as string[]).includes("MAPOS_RESTAURANT_V2") &&
    !allowedWorkflows.includes("MAPOS_RESTAURANT")
  ) {
    allowedWorkflows.push("MAPOS_RESTAURANT");
  }
  if (!allowedWorkflows.includes("NONE")) allowedWorkflows.unshift("NONE");
  const requestedWorkflow =
    (company.agentWorkflow as string) === "MAPOS_RESTAURANT_V2"
      ? "MAPOS_RESTAURANT"
      : company.agentWorkflow || "NONE";
  // A missing assignment list means no integrations are enabled. Falling back
  // to every capability made editing a legacy company silently grant all of them.
  if (
    companyType === "BASIC" &&
    requestedWorkflow === "LOANS" &&
    allowedWorkflows.includes("LOANS") &&
    !allowedIntegrations.includes(IntegrationCodeEnum.BANCHAT)
  ) {
    allowedIntegrations.push(IntegrationCodeEnum.BANCHAT);
  }

  return {
    ...createDefaultCompany(),
    ...company,
    countryCode: company.countryCode || "CO",
    countryName: company.countryName || "Colombia",
    defaultCurrency: company.defaultCurrency || "COP",
    phoneNumberId:
      company.phoneNumberId ||
      company.metaConnections?.whatsapp?.phoneNumberId ||
      "",
    whatsappId:
      company.whatsappId || company.metaConnections?.whatsapp?.wabaId || "",
    billing: normalizeBilling(company.billing),
    companyType,
    allowedIntegrations,
    allowedWorkflows,
    agentWorkflow: allowedWorkflows.includes(requestedWorkflow)
      ? requestedWorkflow
      : allowedWorkflows[0],
    schedule: normalizeSchedule(company.schedule),
  };
};

const toUserUpdateInput = (user: AdminUser): AdminUserUpdateInput => ({
  userId: user.userId,
  fullName: user.fullName,
  company: user.company,
  role: user.role,
  integrations: user.integrations,
  isActive: user.isActive,
});

const getDateTime = (value?: string) => {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

const formatUserActivity = (user: AdminUser) => {
  const lastActiveAt = getDateTime(
    user.lastActiveAt || user.lastRefreshAt || user.lastSignInAt || user.lastLoginAt,
  );
  if (!lastActiveAt) return "Sin registro";
  return new Intl.DateTimeFormat("es-VE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(lastActiveAt);
};

const timeFieldSx = {
  "& input::-webkit-calendar-picker-indicator": { display: "none" },
} as const;

const indexChipSx = {
  height: 20,
  fontSize: "0.68rem",
  fontWeight: 500,
  "& .MuiChip-label": {
    px: 0.75,
    lineHeight: 1,
  },
} as const;

const AdminPage = () => {
  const [tab, setTab] = useState(0);
  const [companyTab, setCompanyTab] = useState(0);
  const [editCompanyTab, setEditCompanyTab] = useState(0);
  const [companies, setCompanies] = useState<AdminCompany[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [plans, setPlans] = useState<AdminBillingPlan[]>([]);
  const [travelRequests, setTravelRequests] = useState<TravelBookingRequest[]>([]);
  const [loadingTravelRequests, setLoadingTravelRequests] = useState(false);
  const [gatewayDraft, setGatewayDraft] = useState<AdminPaymentGateway>(createDefaultGateway);
  const [company, setCompany] = useState<AdminCompanyInput>(createDefaultCompany);
  const [planDraft, setPlanDraft] = useState<AdminBillingPlanInput>(createDefaultPlan);
  const [user, setUser] = useState<AdminUserInput>(createDefaultUser);
  const [creatingCompany, setCreatingCompany] = useState(false);
  const [creatingPlan, setCreatingPlan] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [editingCompany, setEditingCompany] = useState<AdminCompany | null>(null);
  const [editingPlan, setEditingPlan] = useState<AdminBillingPlan | null>(null);
  const [companyDraft, setCompanyDraft] = useState<AdminCompanyInput>(createDefaultCompany);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [userDraft, setUserDraft] = useState<AdminUserUpdateInput | null>(null);
  const [passwordUser, setPasswordUser] = useState<AdminUser | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [loadingGateways, setLoadingGateways] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validatingCompanyWhatsapp, setValidatingCompanyWhatsapp] = useState(false);
  const [processingEncryption, setProcessingEncryption] = useState(false);
  const [companyEncryptionUnlocked, setCompanyEncryptionUnlocked] = useState(false);
  const [companyDraftEncryptionUnlocked, setCompanyDraftEncryptionUnlocked] = useState(false);
  const [companyEncryptionPlainText, setCompanyEncryptionPlainText] = useState("");
  const [companyDraftEncryptionPlainText, setCompanyDraftEncryptionPlainText] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [companySort, setCompanySort] = useState<{
    key: "name" | "companyId" | "companyType" | "countryCode" | "billing" | "payment" | "agentWorkflow";
    direction: "asc" | "desc";
  }>({ key: "name", direction: "asc" });
  const [planSort, setPlanSort] = useState<{
    key: "name" | "integrationCode" | "currency" | "amount" | "interval" | "isActive";
    direction: "asc" | "desc";
  }>({ key: "integrationCode", direction: "asc" });
  const [userSort, setUserSort] = useState<{
    key: "fullName" | "email" | "company" | "role" | "isActive" | "activity" | "integrations";
    direction: "asc" | "desc";
  }>({ key: "fullName", direction: "asc" });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const companyById = useMemo(
    () => new Map(companies.map((item) => [item.companyId, item])),
    [companies],
  );
  const normalizeLookupText = (value: string) => value.trim().toLowerCase();
  const resolveCompanyId = (value: string) => {
    const cleanValue = value.trim();
    if (!cleanValue) return "";
    if (companyById.has(cleanValue)) return cleanValue;
    return (
      companies.find(
        (item) =>
          normalizeLookupText(item.name || "") === normalizeLookupText(cleanValue) ||
          normalizeLookupText(item.companyId || "") === normalizeLookupText(cleanValue),
      )?.companyId || cleanValue
    );
  };
  const resolveCompanyName = (value: string) => {
    const companyId = resolveCompanyId(value);
    return companyById.get(companyId)?.name || value;
  };
  const compareText = (left: unknown, right: unknown) =>
    String(left || "").localeCompare(String(right || ""), "es", {
      numeric: true,
      sensitivity: "base",
    });
  const sortedCompanies = useMemo(() => {
    const direction = companySort.direction === "asc" ? 1 : -1;
    return [...companies].sort((left, right) => {
      const getValue = (item: AdminCompany) => {
        if (companySort.key === "billing") {
          return `${item.billing?.currency || ""} ${item.billing?.amount || 0} ${item.billing?.status || ""}`;
        }
        if (companySort.key === "payment") {
          return `${item.billing?.status || ""} ${item.billing?.paymentStatus || ""} ${item.billing?.lastPaymentAt || ""}`;
        }
        return item[companySort.key];
      };
      return compareText(getValue(left), getValue(right)) * direction;
    });
  }, [companies, companySort]);
  const sortedUsers = useMemo(() => {
    const direction = userSort.direction === "asc" ? 1 : -1;
    return [...users].sort((left, right) => {
      const getValue = (item: AdminUser) => {
        if (userSort.key === "company") {
          const companyId = item.company.trim();
          return companyById.get(companyId)?.name || item.company;
        }
        if (userSort.key === "activity") {
          return getDateTime(item.lastActiveAt || item.lastRefreshAt || item.lastSignInAt || item.lastLoginAt)?.getTime() || 0;
        }
        if (userSort.key === "integrations") return item.integrations.join(" ");
        return item[userSort.key];
      };
      return compareText(getValue(left), getValue(right)) * direction;
    });
  }, [companyById, userSort, users]);
  const sortedPlans = useMemo(() => {
    const direction = planSort.direction === "asc" ? 1 : -1;
    return [...plans].sort(
      (left, right) => compareText(left[planSort.key], right[planSort.key]) * direction,
    );
  }, [planSort, plans]);
  const gatewayWebhookUrl = useMemo(() => {
    const path = gatewayDraft.webhookUrl || "/billing/webhooks/bold";
    if (path.startsWith("http")) return path;
    return `${getConfiguredApiBaseUrl()}${path}`;
  }, [gatewayDraft.webhookUrl]);

  const toggleCompanySort = (key: typeof companySort.key) =>
    setCompanySort((current) => ({
      key,
      direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  const toggleUserSort = (key: typeof userSort.key) =>
    setUserSort((current) => ({
      key,
      direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  const togglePlanSort = (key: typeof planSort.key) =>
    setPlanSort((current) => ({
      key,
      direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));

  const loadCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const result = await withTimeout(
        adminApi.listCompanies.requestHard(null),
        "La consulta de compañías",
      );
      if (result.isSuccess) setCompanies(result.data || []);
      setError(result.messageError || "");
    } finally {
      setLoadingCompanies(false);
    }
  };

  const loadTravelRequests = async () => {
    setLoadingTravelRequests(true);
    const result = await adminApi.listTravelRequests.requestHard(null);
    if (result.isSuccess) setTravelRequests(result.data || []);
    else setError(result.messageError || "No se pudieron cargar las solicitudes de Travel.");
    setLoadingTravelRequests(false);
  };

  const updateGlobalTravelStatus = async (booking: TravelBookingRequest, status: TravelBookingStatus) => {
    const result = await adminApi.updateTravelRequestStatus(booking.companyId, booking.id).requestHard({ status });
    if (!result.isSuccess) {
      setError(result.messageError || "No se pudo actualizar la solicitud.");
      return;
    }
    setSuccess("Estado actualizado. La acción quedó registrada en auditoría.");
    await loadTravelRequests();
  };

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const result = await withTimeout(
        adminApi.listUsers.requestHard(null),
        "La consulta de usuarios",
      );
      if (result.isSuccess) setUsers(result.data || []);
      setError(result.messageError || "");
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadPlans = async () => {
    setLoadingPlans(true);
    try {
      const result = await withTimeout(
        adminApi.listBillingPlans.requestHard(null),
        "La consulta de planes",
      );
      if (result.isSuccess) setPlans(result.data || []);
      setError(result.messageError || "");
    } finally {
      setLoadingPlans(false);
    }
  };

  const loadGateways = async () => {
    setLoadingGateways(true);
    try {
      const result = await withTimeout(
        adminApi.listPaymentGateways.requestHard(null),
        "La consulta de pasarelas",
      );
      if (result.isSuccess) {
        setGatewayDraft({
          ...createDefaultGateway(),
          ...(result.data || []).find((gateway) => gateway.provider === "BOLD"),
        });
      }
      setError(result.messageError || "");
    } finally {
      setLoadingGateways(false);
    }
  };

  useEffect(() => {
    void loadCompanies();
    void loadUsers();
    void loadPlans();
    void loadGateways();
  }, []);

  const updateCompany = (field: keyof AdminCompanyInput, value: unknown) =>
    setCompany((current) => ({ ...current, [field]: value }));

  const updateCompanyDraft = (field: keyof AdminCompanyInput, value: unknown) =>
    setCompanyDraft((current) => ({ ...current, [field]: value }));

  const updateGatewayDraft = (field: keyof AdminPaymentGateway, value: unknown) =>
    setGatewayDraft((current) => ({ ...current, [field]: value }));

  const updateCompanyCountry = (
    countryCode: string,
    onChange: (field: keyof AdminCompanyInput, nextValue: unknown) => void,
  ) => {
    const selectedCountry = COUNTRY_OPTIONS.find((item) => item.code === countryCode);
    onChange("countryCode", countryCode);
    onChange("countryName", selectedCountry?.name || "");
    onChange("defaultCurrency", selectedCountry?.currency || "USD");
  };

  const updateCompanyBilling = (
    value: AdminCompanyInput,
    field: keyof AdminCompanyBillingInput,
    nextValue: unknown,
    onChange: (field: keyof AdminCompanyInput, nextValue: unknown) => void,
  ) => {
    onChange("billing", {
      ...normalizeBilling(value.billing),
      [field]: nextValue,
      provider: "BOLD",
    });
  };

  const applyPlanToCompany = (
    value: AdminCompanyInput,
    planId: string,
    onChange: (field: keyof AdminCompanyInput, nextValue: unknown) => void,
  ) => {
    const selectedPlan = plans.find((item) => item.planId === planId);
    if (!selectedPlan) {
      updateCompanyBilling(value, "planId", planId, onChange);
      return;
    }
    onChange("billing", {
      ...normalizeBilling(value.billing),
      planId: selectedPlan.planId,
      planName: selectedPlan.name,
      integrationCode: selectedPlan.integrationCode,
      currency: selectedPlan.currency,
      amount: selectedPlan.amount,
      interval: selectedPlan.interval,
      provider: "BOLD",
    });
  };

  const changeCompanyType = (
    nextType: CompanyType,
    value: AdminCompanyInput,
    onChange: (field: keyof AdminCompanyInput, nextValue: unknown) => void,
  ) => {
    const capabilities = COMPANY_TYPE_CAPABILITIES[nextType];
    const allowedIntegrations = value.allowedIntegrations.filter((integration) =>
      capabilities.integrations.includes(integration),
    );
    const allowedWorkflows = value.allowedWorkflows.filter((workflow) =>
      capabilities.workflows.includes(workflow),
    );
    if (!allowedWorkflows.includes("NONE")) allowedWorkflows.unshift("NONE");
    onChange("companyType", nextType);
    onChange("allowedIntegrations", allowedIntegrations);
    onChange("allowedWorkflows", allowedWorkflows);
    if (!allowedWorkflows.includes(value.agentWorkflow)) {
      onChange("agentWorkflow", "NONE");
    }
  };

  const getAllowedIntegrationsForCompany = (companyId: string) => {
    const selectedCompany = companyById.get(resolveCompanyId(companyId));
    if (!selectedCompany) return [];
    return selectedCompany.allowedIntegrations || [];
  };

  const getDefaultIntegrationsForCompany = (companyId: string) => {
    const selectedCompany = companyById.get(resolveCompanyId(companyId));
    const allowedIntegrations = getAllowedIntegrationsForCompany(companyId);
    return selectedCompany?.agentWorkflow === "LOANS" &&
      allowedIntegrations.includes(IntegrationCodeEnum.BANCHAT)
      ? [IntegrationCodeEnum.BANCHAT]
      : [];
  };

  const getIntegrationLabel = (integration: string) =>
    integration === IntegrationCodeEnum.BANCHAT
      ? "BanChat (Chats y WhatsApp/API)"
      : integration === IntegrationCodeEnum.LYPRO_MAPOS_NOTIFICATIONS_BOT
        ? "LYPRO-MAPOS-NOTIFICATIONS-BOT"
      : integration;

  const hasSensitiveChanges = (current: AdminCompany, draft: AdminCompanyInput) =>
    SENSITIVE_FIELDS.some((field) => String(current[field] || "") !== String(draft[field] || ""));

  const copyValue = async (value: string) => {
    if (!value) return;
    await navigator.clipboard.writeText(value);
    setSuccess("Valor copiado al portapapeles.");
  };

  const isEditingCompanyForm = (disabledCompanyId?: boolean) => Boolean(disabledCompanyId);

  const getEncryptionPlainText = (disabledCompanyId?: boolean, value?: AdminCompanyInput) =>
    isEditingCompanyForm(disabledCompanyId)
      ? companyDraftEncryptionPlainText || value?.idMapos || ""
      : companyEncryptionPlainText || value?.idMapos || "";

  const setEncryptionPlainText = (disabledCompanyId: boolean | undefined, nextValue: string) => {
    if (isEditingCompanyForm(disabledCompanyId)) {
      setCompanyDraftEncryptionPlainText(nextValue);
      return;
    }
    setCompanyEncryptionPlainText(nextValue);
  };

  const isEncryptionUnlocked = (disabledCompanyId?: boolean) =>
    isEditingCompanyForm(disabledCompanyId)
      ? companyDraftEncryptionUnlocked
      : companyEncryptionUnlocked;

  const setEncryptionUnlocked = (disabledCompanyId: boolean | undefined, nextValue: boolean) => {
    if (isEditingCompanyForm(disabledCompanyId)) {
      setCompanyDraftEncryptionUnlocked(nextValue);
      return;
    }
    setCompanyEncryptionUnlocked(nextValue);
  };

  const toggleEncryptionUnlocked = (disabledCompanyId?: boolean) => {
    const nextValue = !isEncryptionUnlocked(disabledCompanyId);
    if (
      nextValue &&
      !window.confirm(
        "Vas a desbloquear campos cifrados del ID Mapos. Hazlo solo si necesitas reemplazarlos o revisar el valor descifrado.",
      )
    ) {
      return;
    }
    setEncryptionUnlocked(disabledCompanyId, nextValue);
  };

  const encryptCompanyIdentifier = async ({
    value,
    onChange,
    disabledCompanyId,
  }: {
    value: AdminCompanyInput;
    onChange: (field: keyof AdminCompanyInput, nextValue: unknown) => void;
    disabledCompanyId?: boolean;
  }) => {
    const idMapos = getEncryptionPlainText(disabledCompanyId, value).trim();
    if (!idMapos) {
      setError("Escribe el ID Mapos que quieres cifrar.");
      return;
    }
    const hasCurrentEncryption = Boolean(value.iv || value.encryptedData);
    if (
      hasCurrentEncryption &&
      !window.confirm("Esto reemplazará el IV y Encrypted data actuales. ¿Confirmas el reemplazo?")
    ) {
      return;
    }
    setError("");
    setSuccess("");
    setProcessingEncryption(true);
    const result = await adminApi.encryptCompanyId.requestHard({ idMapos });
    setProcessingEncryption(false);
    if (!result.isSuccess || !result.data) {
      setError(result.messageError || "No se pudo cifrar el ID Mapos.");
      return;
    }
    onChange("idMapos", idMapos);
    onChange("iv", result.data.iv);
    onChange("encryptedData", result.data.encryptedData);
    setSuccess("ID Mapos cifrado. Revisa y guarda la compañía para persistirlo.");
  };

  const decryptCompanyIdentifier = async ({
    value,
    disabledCompanyId,
  }: {
    value: AdminCompanyInput;
    disabledCompanyId?: boolean;
  }) => {
    if (!isEncryptionUnlocked(disabledCompanyId)) {
      setError("Abre el candado antes de descifrar el ID Mapos.");
      return;
    }
    if (!value.iv || !value.encryptedData) {
      setError("IV y Encrypted data son requeridos para descifrar.");
      return;
    }
    setError("");
    setSuccess("");
    setProcessingEncryption(true);
    const result = await adminApi.decryptCompanyId.requestHard({
      iv: value.iv,
      encryptedData: value.encryptedData,
    });
    setProcessingEncryption(false);
    if (!result.isSuccess || !result.data) {
      setError(result.messageError || "No se pudo descifrar el ID Mapos.");
      return;
    }
    setEncryptionPlainText(disabledCompanyId, result.data.idMapos || result.data.companyId || "");
    setSuccess("ID Mapos descifrado en el campo editable.");
  };

  const getCompanyWithVerifiedMaposEncryption = async (
    value: AdminCompanyInput,
    onChange: (field: keyof AdminCompanyInput, nextValue: unknown) => void,
  ) => {
    if (value.companyType !== "MAPOS") return value;
    if (value.iv || value.encryptedData) {
      if (!value.iv || !value.encryptedData) {
        setError("IV y Encrypted data deben estar completos. Genera el cifrado de nuevo o completa ambos campos.");
        return null;
      }

      const decryptResult = await adminApi.decryptCompanyId.requestHard({
        iv: value.iv,
        encryptedData: value.encryptedData,
      });
      if (!decryptResult.isSuccess || !decryptResult.data) {
        setError(decryptResult.messageError || "No se pudo validar el cifrado del ID Mapos.");
        return null;
      }

      const decryptedIdMapos = (decryptResult.data.idMapos || decryptResult.data.companyId || "").trim();
      const currentIdMapos = value.idMapos.trim();
      if (!currentIdMapos && decryptedIdMapos) {
        onChange("idMapos", decryptedIdMapos);
        return { ...value, idMapos: decryptedIdMapos };
      }
      if (currentIdMapos !== decryptedIdMapos) {
        setError(
          `El cifrado descifra "${decryptedIdMapos}", pero el ID Mapos es "${currentIdMapos}". Genera el cifrado de nuevo antes de guardar.`,
        );
        return null;
      }
      return value;
    }

    if (!value.idMapos.trim()) return value;

    const result = await adminApi.encryptCompanyId.requestHard({ idMapos: value.idMapos.trim() });
    if (!result.isSuccess || !result.data) {
      setError(result.messageError || "No se pudo generar el cifrado del ID Mapos.");
      return null;
    }

    onChange("iv", result.data.iv);
    onChange("encryptedData", result.data.encryptedData);
    return { ...value, iv: result.data.iv, encryptedData: result.data.encryptedData };
  };

  const submitCompany = async () => {
    setError("");
    setSuccess("");
    setSaving(true);
    const companyToCreate = await getCompanyWithVerifiedMaposEncryption(company, updateCompany);
    if (!companyToCreate) {
      setSaving(false);
      return;
    }
    const result = await adminApi.createCompany.requestHard({ company: companyToCreate });
    setSaving(false);
    if (!result.isSuccess) {
      setError(result.messageError || "No se pudo crear la compañía.");
      return;
    }
    setSuccess("Compañía creada.");
    setCompany(createDefaultCompany());
    setCompanyEncryptionUnlocked(false);
    setCompanyEncryptionPlainText("");
    setCreatingCompany(false);
    setCompanyTab(0);
    await loadCompanies();
  };

  const submitCompanyEdit = async () => {
    if (!editingCompany) return;
    if (
      hasSensitiveChanges(editingCompany, companyDraft) &&
      !window.confirm("Vas a cambiar credenciales sensibles de esta compañía. ¿Confirmas el guardado?")
    ) {
      return;
    }
    setError("");
    setSuccess("");
    setSaving(true);
    const companyToUpdate = await getCompanyWithVerifiedMaposEncryption(
      { ...companyDraft, companyId: editingCompany.companyId },
      updateCompanyDraft,
    );
    if (!companyToUpdate) {
      setSaving(false);
      return;
    }
    const result = await adminApi.updateCompany(editingCompany.companyId).requestHard({
      company: companyToUpdate,
    });
    setSaving(false);
    if (!result.isSuccess) {
      setError(result.messageError || "No se pudo actualizar la compañía.");
      return;
    }
    setSuccess("Compañía actualizada.");
    setEditingCompany(null);
    setCompanyDraftEncryptionUnlocked(false);
    setCompanyDraftEncryptionPlainText("");
    await loadCompanies();
  };

  const saveAndValidateCompanyWhatsapp = async () => {
    if (!editingCompany) return;
    if (
      hasSensitiveChanges(editingCompany, companyDraft) &&
      !window.confirm("Vas a cambiar credenciales sensibles de esta compañía. ¿Confirmas el guardado?")
    ) {
      return;
    }
    setError("");
    setSuccess("");
    setSaving(true);
    const companyToUpdate = await getCompanyWithVerifiedMaposEncryption(
      { ...companyDraft, companyId: editingCompany.companyId },
      updateCompanyDraft,
    );
    if (!companyToUpdate) {
      setSaving(false);
      return;
    }
    const saved = await adminApi.updateCompany(editingCompany.companyId).requestHard({
      company: companyToUpdate,
    });
    if (!saved.isSuccess) {
      setSaving(false);
      setError(saved.messageError || "No se pudieron guardar las credenciales de WhatsApp.");
      return;
    }

    setValidatingCompanyWhatsapp(true);
    const validation = await adminApi
      .validateCompanyWhatsApp(editingCompany.companyId)
      .requestHard(null);
    setValidatingCompanyWhatsapp(false);
    setSaving(false);
    if (!validation.isSuccess) {
      setError(`Las credenciales quedaron guardadas, pero Meta no pudo validarlas: ${validation.messageError || "revisa el token y los IDs."}`);
      await loadCompanies();
      return;
    }
    setSuccess("Credenciales guardadas. Meta validó el WABA y LyPro quedó suscrito a sus webhooks.");
    await loadCompanies();
  };

  const submitUser = async () => {
    setError("");
    setSuccess("");
    setSaving(true);
    const result = await adminApi.createUser.requestHard({ user });
    setSaving(false);
    if (!result.isSuccess) {
      setError(result.messageError || "No se pudo crear el usuario.");
      return;
    }
    setSuccess("Usuario creado en Firebase Authentication y en el perfil de Lypro.");
    setUser(createDefaultUser());
    setCreatingUser(false);
    await loadUsers();
  };

  const submitUserEdit = async () => {
    if (!editingUser || !userDraft) return;
    setError("");
    setSuccess("");
    setSaving(true);
    const result = await adminApi.updateUser(editingUser.userId).requestHard({ user: userDraft });
    setSaving(false);
    if (!result.isSuccess) {
      setError(result.messageError || "No se pudo actualizar el usuario.");
      return;
    }
    setSuccess("Usuario actualizado.");
    setEditingUser(null);
    setUserDraft(null);
    await loadUsers();
  };

  const submitPassword = async () => {
    if (!passwordUser) return;
    if (newPassword.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres.");
      return;
    }
    setError("");
    setSuccess("");
    setSaving(true);
    const result = await adminApi.updateUserPassword(passwordUser.userId).requestHard({
      password: newPassword,
    });
    setSaving(false);
    if (!result.isSuccess) {
      setError(result.messageError || "No se pudo actualizar la contraseña.");
      return;
    }
    setSuccess(`Contraseña actualizada para ${passwordUser.email}.`);
    setPasswordUser(null);
    setNewPassword("");
  };

  const deleteUser = async (item: AdminUser) => {
    if (!window.confirm(`¿Eliminar definitivamente a ${item.fullName || item.email}? También se eliminará su acceso de Firebase Authentication.`)) return;
    setError("");
    setSuccess("");
    setDeletingId(`user:${item.userId}`);
    const result = await adminApi.deleteUser(item.userId).requestHard(null);
    setDeletingId("");
    if (!result.isSuccess) {
      setError(result.messageError || "No se pudo eliminar el usuario.");
      return;
    }
    setSuccess("Usuario eliminado.");
    await loadUsers();
  };

  const submitPlan = async () => {
    setError("");
    setSuccess("");
    setSaving(true);
    const payload = {
      ...planDraft,
      features: planDraft.features.map((feature) => feature.trim()).filter(Boolean),
      amount: Number(planDraft.amount || 0),
    };
    const result = editingPlan
      ? await adminApi.updateBillingPlan(editingPlan.planId).requestHard({ plan: payload })
      : await adminApi.createBillingPlan.requestHard({ plan: payload });
    setSaving(false);
    if (!result.isSuccess) {
      setError(result.messageError || "No se pudo guardar el plan.");
      return;
    }
    setSuccess(editingPlan ? "Plan actualizado." : "Plan creado.");
    setCreatingPlan(false);
    setEditingPlan(null);
    setPlanDraft(createDefaultPlan());
    await loadPlans();
  };

  const submitGateway = async () => {
    setError("");
    setSuccess("");
    setSaving(true);
    const result = await adminApi.updatePaymentGateway("BOLD").requestHard({
      gateway: gatewayDraft,
    });
    setSaving(false);
    if (!result.isSuccess || !result.data) {
      setError(result.messageError || "No se pudo guardar la pasarela.");
      return;
    }
    setGatewayDraft({ ...createDefaultGateway(), ...result.data });
    setSuccess("Pasarela Bold actualizada.");
  };

  const deletePlan = async (item: AdminBillingPlan) => {
    if (!window.confirm(`¿Eliminar el plan ${item.name}? Solo se puede eliminar si no está asignado.`)) return;
    setError("");
    setSuccess("");
    setDeletingId(`plan:${item.planId}`);
    const result = await adminApi.deleteBillingPlan(item.planId).requestHard(null);
    setDeletingId("");
    if (!result.isSuccess) {
      setError(result.messageError || "No se pudo eliminar el plan.");
      return;
    }
    setSuccess("Plan eliminado.");
    await loadPlans();
  };

  const deleteCompany = async (item: AdminCompany) => {
    const assignedUsers = users.filter(
      (userItem) => resolveCompanyId(userItem.company) === item.companyId,
    );
    if (assignedUsers.length) {
      setError(`Primero elimina los ${assignedUsers.length} usuario(s) asignados a ${item.name || item.companyId}.`);
      setTab(1);
      return;
    }
    if (!window.confirm(`¿Eliminar la compañía ${item.name || item.companyId}? Se eliminará su configuración, pero se conservarán chats, pedidos y datos históricos.`)) return;
    setError("");
    setSuccess("");
    setDeletingId(`company:${item.companyId}`);
    const result = await adminApi.deleteCompany(item.companyId).requestHard(null);
    setDeletingId("");
    if (!result.isSuccess) {
      setError(result.messageError || "No se pudo eliminar la compañía.");
      return;
    }
    setSuccess("Compañía eliminada. Los datos históricos se conservaron.");
    await loadCompanies();
  };

  const openCompanyEdit = (item: AdminCompany) => {
    setEditingCompany(item);
    setCompanyDraft(toCompanyInput(item));
    setCompanyDraftEncryptionUnlocked(false);
    setCompanyDraftEncryptionPlainText(item.idMapos || "");
    setEditCompanyTab(0);
  };

  const openCompanyCreate = () => {
    setCompany(createDefaultCompany());
    setCompanyEncryptionUnlocked(false);
    setCompanyEncryptionPlainText("");
    setCompanyTab(0);
    setCreatingCompany(true);
  };

  const closeCompanyCreate = () => {
    setCreatingCompany(false);
    setCompany(createDefaultCompany());
    setCompanyEncryptionUnlocked(false);
    setCompanyEncryptionPlainText("");
    setCompanyTab(0);
  };

  const openUserCreate = () => {
    setUser(createDefaultUser());
    setCreatingUser(true);
  };

  const openPlanCreate = () => {
    setPlanDraft(createDefaultPlan());
    setEditingPlan(null);
    setCreatingPlan(true);
  };

  const openPlanEdit = (item: AdminBillingPlan) => {
    setPlanDraft({
      ...createDefaultPlan(),
      ...item,
      features: item.features || [],
      amount: Number(item.amount || 0),
      isActive: item.isActive !== false,
    });
    setEditingPlan(item);
    setCreatingPlan(true);
  };

  const closeUserCreate = () => {
    setCreatingUser(false);
    setUser(createDefaultUser());
  };

  const openUserEdit = (item: AdminUser) => {
    setEditingUser(item);
    setUserDraft({
      ...toUserUpdateInput(item),
      company: resolveCompanyId(item.company),
    });
  };

  const renderCompanyTabs = (
    value: number,
    onChange: (nextValue: number) => void,
    companyType: CompanyType,
  ) => (
    <ToggleButtonGroup
      value={value}
      exclusive
      size="small"
      onChange={(_, nextValue) => {
        if (nextValue !== null) onChange(nextValue);
      }}
      sx={{
        p: 0.5,
        mb: 2,
        width: "100%",
        maxWidth: "100%",
        overflowX: "auto",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1.5,
        bgcolor: "background.paper",
        "& .MuiToggleButtonGroup-grouped": {
          border: 0,
          borderRadius: "8px !important",
          mx: 0,
        },
        "& .MuiToggleButton-root": {
          flex: "0 0 auto",
          px: { xs: 1.25, sm: 1.75 },
          minHeight: 38,
          textTransform: "none",
          gap: 0.75,
        },
      }}
    >
      <ToggleButton value={0}><Person fontSize="small" />Perfil</ToggleButton>
      {companyType === "MAPOS" && (
        <ToggleButton value={1}><MenuBook fontSize="small" />Menú</ToggleButton>
      )}
      <ToggleButton value={2}><AccessTime fontSize="small" />Horarios</ToggleButton>
      {companyType === "MAPOS" && (
        <ToggleButton value={3}><ShoppingBag fontSize="small" />Pedidos</ToggleButton>
      )}
      <ToggleButton value={4}><WhatsApp fontSize="small" />WhatsApp API</ToggleButton>
      <ToggleButton value={5}><Key fontSize="small" />IA</ToggleButton>
    </ToggleButtonGroup>
  );

  const renderCopyAdornment = (value: string) => (
    <InputAdornment position="end">
      <Tooltip title="Copiar">
        <Button size="small" onClick={() => void copyValue(value)} sx={{ minWidth: 0, px: 1 }}>
          <ContentCopy fontSize="small" />
        </Button>
      </Tooltip>
    </InputAdornment>
  );

  const renderScheduleEditor = (
    value: AdminCompanyInput,
    onChange: (field: keyof AdminCompanyInput, nextValue: unknown) => void,
  ) => {
    const schedule = normalizeSchedule(value.schedule);
    return (
      <Stack spacing={2}>
        <FormControl fullWidth>
          <InputLabel id="admin-schedule-mode-label">Tipo de horario</InputLabel>
          <Select
            labelId="admin-schedule-mode-label"
            label="Tipo de horario"
            value={schedule.mode}
            onChange={(event) => {
              onChange(
                "schedule",
                event.target.value === "fixed"
                  ? defaultSchedule()
                  : {
                      mode: "variable",
                      days: DAYS.reduce(
                        (acc, day) => ({ ...acc, [day]: { opening: "08:00", closing: "21:00" } }),
                        {},
                      ),
                    },
              );
            }}
          >
            <MenuItem value="fixed">Fijo para todos los días</MenuItem>
            <MenuItem value="variable">Variable por día</MenuItem>
          </Select>
        </FormControl>

        {schedule.mode === "fixed" ? (
          <Grid container spacing={1.5}>
            {(["opening", "closing"] as const).map((field) => (
              <Grid item xs={12} md={6} key={field}>
                <TextField
                  fullWidth
                  label={field === "opening" ? "Hora de apertura" : "Hora de cierre"}
                  type="time"
                  value={schedule.fixed[field]}
                  onChange={(event) =>
                    onChange("schedule", {
                      mode: "fixed",
                      fixed: { ...schedule.fixed, [field]: event.target.value },
                    })
                  }
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: { step: 60 },
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <AccessTime sx={{ color: "text.secondary" }} />
                        </InputAdornment>
                      ),
                    },
                  }}
                  sx={timeFieldSx}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Stack spacing={1}>
            {DAYS.map((day) => {
              const dayValue = schedule.days[day] ?? null;
              const isOpen = Boolean(dayValue);
              return (
                <Grid container spacing={1.5} alignItems="center" key={day}>
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isOpen}
                          onChange={(event) =>
                            onChange("schedule", {
                              mode: "variable",
                              days: {
                                ...schedule.days,
                                [day]: event.target.checked
                                  ? { opening: "08:00", closing: "21:00" }
                                  : null,
                              },
                            })
                          }
                        />
                      }
                      label={day.charAt(0).toUpperCase() + day.slice(1)}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField
                      fullWidth
                      disabled={!isOpen}
                      label="Apertura"
                      type="time"
                      value={dayValue?.opening || ""}
                      onChange={(event) =>
                        onChange("schedule", {
                          mode: "variable",
                          days: {
                            ...schedule.days,
                            [day]: {
                              ...(dayValue || { opening: "08:00", closing: "21:00" }),
                              opening: event.target.value,
                            },
                          },
                        })
                      }
                      slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: 60 } }}
                      sx={timeFieldSx}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField
                      fullWidth
                      disabled={!isOpen}
                      label="Cierre"
                      type="time"
                      value={dayValue?.closing || ""}
                      onChange={(event) =>
                        onChange("schedule", {
                          mode: "variable",
                          days: {
                            ...schedule.days,
                            [day]: {
                              ...(dayValue || { opening: "08:00", closing: "21:00" }),
                              closing: event.target.value,
                            },
                          },
                        })
                      }
                      slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: 60 } }}
                      sx={timeFieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Button
                      fullWidth
                      variant="outlined"
                      disabled={!isOpen}
                      onClick={() => {
                        const days = { ...schedule.days };
                        DAYS.forEach((otherDay) => {
                          if (days[otherDay] !== null) days[otherDay] = dayValue;
                        });
                        onChange("schedule", { mode: "variable", days });
                      }}
                    >
                      Copiar a abiertos
                    </Button>
                  </Grid>
                </Grid>
              );
            })}
          </Stack>
        )}
      </Stack>
    );
  };

  const renderCompanyFormContent = ({
    value,
    onChange,
    activeTab,
    disabledCompanyId,
  }: {
    value: AdminCompanyInput;
    onChange: (field: keyof AdminCompanyInput, nextValue: unknown) => void;
    activeTab: number;
    disabledCompanyId?: boolean;
  }) => {
    if (activeTab === 0) {
      const typeCapabilities = COMPANY_TYPE_CAPABILITIES[value.companyType];
      let workflowOptions =
        value.companyType === "BASIC" &&
        value.allowedIntegrations.includes(IntegrationCodeEnum.LYPRO_MAPOS_NOTIFICATIONS_BOT)
          ? [...typeCapabilities.workflows, "EXTERNAL_BOT" as AgentWorkflow]
          : typeCapabilities.workflows;
      if (value.allowedIntegrations.includes(IntegrationCodeEnum.LYPRO_TRAVEL)) workflowOptions = [...workflowOptions, "LYPRO_TRAVEL"];
      return (
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight={700}>Tipo de compañía</Typography>
            <Typography variant="body2" color="text.secondary" mb={1}>
              El tipo controla qué integraciones, workflows y campos puede utilizar esta compañía.
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={value.companyType}
              onChange={(_event, nextType: CompanyType | null) => {
                if (nextType) changeCompanyType(nextType, value, onChange);
              }}
              sx={{ flexWrap: "wrap" }}
            >
              {COMPANY_TYPE_OPTIONS.map((option) => (
                <ToggleButton key={option.value} value={option.value} sx={{ textTransform: "none" }}>
                  <Stack alignItems="flex-start">
                    <Typography variant="body2" fontWeight={700}>{option.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{option.description}</Typography>
                  </Stack>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Grid>
          <Grid item xs={12}><Divider /></Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth required label="Company ID" disabled={disabledCompanyId} value={value.companyId} onChange={(event) => onChange("companyId", event.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth required label="Nombre" value={value.name} onChange={(event) => onChange("name", event.target.value)} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField select required fullWidth label="País" value={value.countryCode} onChange={(event) => updateCompanyCountry(event.target.value, onChange)}>
              {COUNTRY_OPTIONS.map((option) => (
                <MenuItem key={option.code} value={option.code}>{option.name}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField select required fullWidth label="Moneda operativa" value={value.defaultCurrency} onChange={(event) => onChange("defaultCurrency", event.target.value)}>
              {CURRENCY_OPTIONS.map((currency) => (
                <MenuItem key={currency} value={currency}>{currency}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField select required fullWidth label="Moneda de cobro" value={normalizeBilling(value.billing).currency} onChange={(event) => updateCompanyBilling(value, "currency", event.target.value, onChange)}>
              {CURRENCY_OPTIONS.map((currency) => (
                <MenuItem key={currency} value={currency}>{currency}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField select fullWidth label="Plan" value={normalizeBilling(value.billing).planId} onChange={(event) => applyPlanToCompany(value, event.target.value, onChange)}>
              <MenuItem value="">Sin plan</MenuItem>
              {plans.filter((plan) => plan.isActive !== false).map((plan) => (
                <MenuItem key={plan.planId} value={plan.planId}>
                  {plan.integrationCode} · {plan.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField fullWidth label="Nombre del plan" value={normalizeBilling(value.billing).planName} onChange={(event) => updateCompanyBilling(value, "planName", event.target.value, onChange)} />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField select fullWidth label="Integración del plan" value={normalizeBilling(value.billing).integrationCode} onChange={(event) => updateCompanyBilling(value, "integrationCode", event.target.value, onChange)}>
              <MenuItem value="">Sin integración</MenuItem>
              {INTEGRATION_OPTIONS.map((integration) => (
                <MenuItem key={integration} value={integration}>{integration}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField fullWidth type="number" label="Monto" value={normalizeBilling(value.billing).amount} onChange={(event) => updateCompanyBilling(value, "amount", Number(event.target.value), onChange)} slotProps={{ htmlInput: { min: 0, step: 1 } }} />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField select fullWidth label="Frecuencia" value={normalizeBilling(value.billing).interval} onChange={(event) => updateCompanyBilling(value, "interval", event.target.value, onChange)}>
              {BILLING_INTERVAL_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField select fullWidth label="Estado" value={normalizeBilling(value.billing).status} onChange={(event) => updateCompanyBilling(value, "status", event.target.value, onChange)}>
              {BILLING_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Link de pago Bold" value={normalizeBilling(value.billing).boldPaymentLink} onChange={(event) => updateCompanyBilling(value, "boldPaymentLink", event.target.value, onChange)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Notas de cobro" value={normalizeBilling(value.billing).notes} onChange={(event) => updateCompanyBilling(value, "notes", event.target.value, onChange)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Nombre del bot" value={value.nameBot} onChange={(event) => onChange("nameBot", event.target.value)} />
          </Grid>
          {value.companyType === "MAPOS" && <Grid item xs={12} md={6}>
            <TextField fullWidth label="ID Mapos" value={value.idMapos} onChange={(event) => onChange("idMapos", event.target.value)} />
          </Grid>}
          <Grid item xs={12} md={3}>
            <TextField select fullWidth label="Región" value={value.regionNumber} onChange={(event) => onChange("regionNumber", Number(event.target.value))}>
              <MenuItem value={0}>Medellín</MenuItem>
              <MenuItem value={1}>Cali</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField select fullWidth label="Workflow" value={value.agentWorkflow} onChange={(event) => {
              const nextWorkflow = event.target.value;
              onChange("agentWorkflow", nextWorkflow);
              if (nextWorkflow === "LOANS" && !value.allowedIntegrations.includes(IntegrationCodeEnum.BANCHAT)) {
                onChange("allowedIntegrations", [...value.allowedIntegrations, IntegrationCodeEnum.BANCHAT]);
              }
            }}>
              {value.allowedWorkflows.map((workflow) => (
                <MenuItem key={workflow} value={workflow}>
                  {WORKFLOW_LABELS[workflow]}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" fontWeight={700}>Workflows permitidos</Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {workflowOptions.map((workflow) => (
                <FormControlLabel
                  key={workflow}
                  control={
                    <Checkbox
                      disabled={workflow === "NONE"}
                      checked={value.allowedWorkflows.includes(workflow)}
                      onChange={(event) => {
                        const nextWorkflows = event.target.checked
                          ? [...value.allowedWorkflows, workflow]
                          : value.allowedWorkflows.filter((item) => item !== workflow);
                        onChange("allowedWorkflows", nextWorkflows);
                        if (!nextWorkflows.includes(value.agentWorkflow)) onChange("agentWorkflow", "NONE");
                      }}
                    />
                  }
                  label={WORKFLOW_LABELS[workflow]}
                />
              ))}
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" fontWeight={700}>Integraciones habilitadas para sus usuarios</Typography>
            <Typography variant="caption" color="text.secondary">
              Al retirar una integración y guardar, también se revocará de los usuarios actuales de la compañía.
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {typeCapabilities.integrations.map((integration) => (
                <FormControlLabel
                  key={integration}
                  control={
                    <Checkbox
                      checked={value.allowedIntegrations.includes(integration)}
                      onChange={(event) => {
                        const integrations = event.target.checked
                          ? [...value.allowedIntegrations, integration]
                          : value.allowedIntegrations.filter((item) => item !== integration);
                        onChange("allowedIntegrations", integrations);
                        if (integration === IntegrationCodeEnum.LYPRO_MAPOS_NOTIFICATIONS_BOT) {
                          const workflows = event.target.checked
                            ? value.allowedWorkflows.includes("EXTERNAL_BOT")
                              ? value.allowedWorkflows
                              : [...value.allowedWorkflows, "EXTERNAL_BOT" as AgentWorkflow]
                            : value.allowedWorkflows.filter((item) => item !== "EXTERNAL_BOT");
                          onChange("allowedWorkflows", workflows);
                          if (!event.target.checked && value.agentWorkflow === "EXTERNAL_BOT") {
                            onChange("agentWorkflow", "NONE");
                          }
                        }
                      }}
                    />
                  }
                  label={getIntegrationLabel(integration)}
                />
              ))}
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline minRows={3} label="Descripción" value={value.description} onChange={(event) => onChange("description", event.target.value)} />
          </Grid>
          {value.companyType === "MAPOS" && (
            <>
              <Grid item xs={12} md={6}>
                <TextField fullWidth multiline minRows={3} label="Recomendaciones" value={value.recommendations} onChange={(event) => onChange("recommendations", event.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth multiline minRows={3} label="Sinónimos" value={value.synonyms} onChange={(event) => onChange("synonyms", event.target.value)} />
              </Grid>
            </>
          )}
        </Grid>
      );
    }

    if (activeTab === 1) {
      return (
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="URL menú" value={value.urlMenu} onChange={(event) => onChange("urlMenu", event.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Sitio web" value={value.websiteUrl} onChange={(event) => onChange("websiteUrl", event.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="URLs de menú (separadas por coma)"
              value={value.urlsMenu.join(", ")}
              onChange={(event) =>
                onChange(
                  "urlsMenu",
                  event.target.value.split(",").map((item) => item.trim()).filter(Boolean),
                )
              }
            />
          </Grid>
        </Grid>
      );
    }

    if (activeTab === 2) return renderScheduleEditor(value, onChange);

    if (activeTab === 3) {
      return (
        <Grid container spacing={1}>
          {[
            ["authMapos", "Autorizar Mapos"],
            ["isReserveEnabled", "Reservas"],
            ["takeOrdersEnabled", "Tomar pedidos"],
            ["websitePickupEnabled", "Pickup web"],
            ["websiteDeliveryEnabled", "Delivery web"],
            ["subCompany", "Subcompañía"],
          ].map(([field, label]) => (
            <Grid item xs={12} sm={6} md={4} key={field}>
              <FormControlLabel
                control={<Switch checked={Boolean(value[field as keyof AdminCompanyInput])} onChange={(event) => onChange(field as keyof AdminCompanyInput, event.target.checked)} />}
                label={label}
              />
            </Grid>
          ))}
        </Grid>
      );
    }

    if (activeTab === 4) {
      return (
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <Alert severity="warning">Estos valores son sensibles. Si los cambias, el panel pedirá confirmación antes de guardar.</Alert>
          </Grid>
          {value.metaConnections?.whatsapp && (
            <Grid item xs={12}>
              <Alert severity={value.metaConnections.whatsapp.status === "ERROR" ? "error" : "info"}>
                Registro insertado de Meta: {value.metaConnections.whatsapp.status || "sin estado"}.
                {value.metaConnections.whatsapp.errorMessage
                  ? ` ${value.metaConnections.whatsapp.errorMessage}`
                  : " Los IDs recibidos de Meta se precargan abajo aunque la conexión no haya terminado."}
              </Alert>
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <TextField fullWidth type="password" label="Access token Meta" value={value.accessToken} helperText="Token de sistema de Meta con permisos de WhatsApp Business Management y Messaging." onChange={(event) => onChange("accessToken", event.target.value)} autoComplete="new-password" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Phone number ID" value={value.phoneNumberId} onChange={(event) => onChange("phoneNumberId", event.target.value)} slotProps={{ input: { endAdornment: renderCopyAdornment(value.phoneNumberId) } }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="WABA ID" value={value.whatsappId} onChange={(event) => onChange("whatsappId", event.target.value)} slotProps={{ input: { endAdornment: renderCopyAdornment(value.whatsappId) } }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Proxy webhook Meta" value={value.metaWebhookProxyUrl} onChange={(event) => onChange("metaWebhookProxyUrl", event.target.value)} slotProps={{ input: { endAdornment: renderCopyAdornment(value.metaWebhookProxyUrl) } }} />
          </Grid>
        </Grid>
      );
    }

    if (value.companyType !== "MAPOS") {
      return (
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <Alert severity="info">La clave de OpenAI se administra de forma central en el backend.</Alert>
          </Grid>
        </Grid>
      );
    }

    const encryptionUnlocked = isEncryptionUnlocked(disabledCompanyId);
    const encryptionPlainText = getEncryptionPlainText(disabledCompanyId, value);
    const encryptedFieldsDisabled = !encryptionUnlocked;

    return (
      <Grid container spacing={1.5}>
        <Grid item xs={12}>
          <Alert severity="warning">
            El IV y el Encrypted data corresponden al ID Mapos cifrado. Abre el candado solo si vas a
            reemplazarlos o descifrarlos.
          </Alert>
        </Grid>
        <Grid item xs={12}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
            <TextField
              fullWidth
              label="ID Mapos a cifrar"
              value={encryptionPlainText}
              onChange={(event) => setEncryptionPlainText(disabledCompanyId, event.target.value)}
            />
            <Tooltip title={encryptionUnlocked ? "Bloquear cifrado" : "Desbloquear cifrado"}>
              <Button
                variant={encryptionUnlocked ? "contained" : "outlined"}
                onClick={() => toggleEncryptionUnlocked(disabledCompanyId)}
                sx={{ minWidth: { xs: "100%", md: 48 } }}
              >
                {encryptionUnlocked ? <LockOpen /> : <Lock />}
              </Button>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={processingEncryption ? <CircularProgress size={14} /> : <Key />}
              disabled={processingEncryption}
              onClick={() =>
                void encryptCompanyIdentifier({ value, onChange, disabledCompanyId })
              }
              sx={{ minWidth: { xs: "100%", md: 150 } }}
            >
              Generar cifrado
            </Button>
            <Button
              variant="outlined"
              startIcon={processingEncryption ? <CircularProgress size={14} /> : <LockOpen />}
              disabled={processingEncryption || encryptedFieldsDisabled}
              onClick={() => void decryptCompanyIdentifier({ value, disabledCompanyId })}
              sx={{ minWidth: { xs: "100%", md: 130 } }}
            >
              Descifrar
            </Button>
          </Stack>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="IV"
            value={value.iv}
            onChange={(event) => onChange("iv", event.target.value)}
            helperText={encryptedFieldsDisabled ? "Bloqueado para evitar reemplazos accidentales." : "Editable con candado abierto."}
            slotProps={{ input: { readOnly: encryptedFieldsDisabled, endAdornment: renderCopyAdornment(value.iv) } }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Encrypted data"
            value={value.encryptedData}
            onChange={(event) => onChange("encryptedData", event.target.value)}
            helperText={encryptedFieldsDisabled ? "Bloqueado para evitar reemplazos accidentales." : "Editable con candado abierto."}
            slotProps={{ input: { readOnly: encryptedFieldsDisabled, endAdornment: renderCopyAdornment(value.encryptedData) } }}
          />
        </Grid>
      </Grid>
    );
  };

  const renderCompaniesTable = () => (
    <TableContainer sx={{ mt: 1, overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: 1320 }}>
        <TableHead>
          <TableRow>
            {[
              ["name", "Compañía"],
              ["companyId", "ID"],
              ["companyType", "Tipo"],
              ["countryCode", "País"],
              ["billing", "Plan"],
              ["payment", "Pago"],
              ["agentWorkflow", "Workflow"],
            ].map(([key, label]) => (
              <TableCell key={key} sortDirection={companySort.key === key ? companySort.direction : false}>
                <TableSortLabel
                  active={companySort.key === key}
                  direction={companySort.key === key ? companySort.direction : "asc"}
                  onClick={() => toggleCompanySort(key as typeof companySort.key)}
                >
                  {label}
                </TableSortLabel>
              </TableCell>
            ))}
            <TableCell align="right">Acciones</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedCompanies.map((item) => (
            <TableRow key={item.companyId} hover>
              <TableCell sx={{ fontWeight: 700 }}>{item.name || item.companyId}</TableCell>
              <TableCell>{item.companyId}</TableCell>
              <TableCell>{item.companyType === "MAPOS" ? "MAPOS" : "Básica"}</TableCell>
              <TableCell>
                <Stack spacing={0.25}>
                  <Typography variant="body2">{item.countryName || item.countryCode || "Colombia"}</Typography>
                  <Typography variant="caption" color="text.secondary">{item.defaultCurrency || "COP"}</Typography>
                </Stack>
              </TableCell>
              <TableCell>
                <Stack spacing={0.25}>
                  <Typography variant="body2">{item.billing?.planName || item.billing?.planId || "Sin plan"}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.billing?.currency || item.defaultCurrency || "COP"} {Number(item.billing?.amount || 0).toLocaleString("es-CO")}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell>
                <Stack spacing={0.5}>
                  <Stack direction="row" gap={0.5} flexWrap="wrap">
                    <Chip size="small" label={item.billing?.status || "trial"} color={item.billing?.status === "active" ? "success" : item.billing?.status === "past_due" ? "error" : "warning"} />
                    <Chip size="small" label={item.billing?.paymentStatus || "pending"} color={item.billing?.paymentStatus === "approved" ? "success" : item.billing?.paymentStatus === "rejected" || item.billing?.paymentStatus === "failed" ? "error" : "default"} variant="outlined" />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Ultimo: {item.billing?.lastPaymentAt ? new Date(item.billing.lastPaymentAt).toLocaleDateString("es-CO") : "sin pago"}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell>{getAgentWorkflowLabel(item.agentWorkflow)}</TableCell>
              <TableCell align="right">
                <Stack direction="row" gap={1} justifyContent="flex-end">
                  <Button variant="outlined" size="small" startIcon={<Edit />} onClick={() => openCompanyEdit(item)}>
                    Editar
                  </Button>
                  <Tooltip title={users.some((userItem) => resolveCompanyId(userItem.company) === item.companyId) ? "Primero elimina los usuarios de esta compañía" : "Eliminar compañía"}>
                    <span>
                      <Button
                        color="error"
                        variant="outlined"
                        size="small"
                        startIcon={deletingId === `company:${item.companyId}` ? <CircularProgress size={14} /> : <DeleteForever />}
                        disabled={Boolean(deletingId)}
                        onClick={() => void deleteCompany(item)}
                      >
                        Eliminar
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
          {!sortedCompanies.length && (
            <TableRow><TableCell colSpan={9}><Typography variant="body2" color="text.secondary" textAlign="center" py={2}>No hay compañías registradas.</Typography></TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderPlansTable = () => (
    <TableContainer sx={{ mt: 1, overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: 920 }}>
        <TableHead>
          <TableRow>
            {[
              ["name", "Plan"],
              ["integrationCode", "Integración"],
              ["currency", "Moneda"],
              ["amount", "Precio"],
              ["interval", "Frecuencia"],
              ["isActive", "Estado"],
            ].map(([key, label]) => (
              <TableCell key={key} sortDirection={planSort.key === key ? planSort.direction : false}>
                <TableSortLabel
                  active={planSort.key === key}
                  direction={planSort.key === key ? planSort.direction : "asc"}
                  onClick={() => togglePlanSort(key as typeof planSort.key)}
                >
                  {label}
                </TableSortLabel>
              </TableCell>
            ))}
            <TableCell align="right">Acciones</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedPlans.map((item) => (
            <TableRow key={item.planId} hover>
              <TableCell sx={{ fontWeight: 700 }}>
                <Stack spacing={0.25}>
                  <Typography variant="body2" fontWeight={700}>{item.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{item.planId}</Typography>
                </Stack>
              </TableCell>
              <TableCell>{item.integrationCode}</TableCell>
              <TableCell>{item.currency}</TableCell>
              <TableCell>{Number(item.amount || 0).toLocaleString("es-CO")}</TableCell>
              <TableCell>{BILLING_INTERVAL_OPTIONS.find((option) => option.value === item.interval)?.label || item.interval}</TableCell>
              <TableCell>
                <Chip size="small" color={item.isActive !== false ? "success" : "default"} label={item.isActive !== false ? "Activo" : "Inactivo"} />
              </TableCell>
              <TableCell align="right">
                <Stack direction="row" gap={1} justifyContent="flex-end">
                  <Button variant="outlined" size="small" startIcon={<Edit />} onClick={() => openPlanEdit(item)}>Editar</Button>
                  <Button
                    color="error"
                    variant="outlined"
                    size="small"
                    startIcon={deletingId === `plan:${item.planId}` ? <CircularProgress size={14} /> : <DeleteForever />}
                    disabled={Boolean(deletingId)}
                    onClick={() => void deletePlan(item)}
                  >
                    Eliminar
                  </Button>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
          {!sortedPlans.length && (
            <TableRow>
              <TableCell colSpan={7}>
                <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>No hay planes registrados.</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderPaymentGatewayCard = () => (
    <Card sx={{ ...dashboardCardSx, width: "100%", mb: 1.5 }}>
      <CardContent sx={{ p: { xs: 1.25, sm: 1.5 } }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={1}>
          <Box>
            <Typography variant="h6" sx={dashboardSectionTitleSx}>Pasarelas de pago</Typography>
            <Typography variant="body2" color="text.secondary">Configura las pasarelas propias de LYPRO para cobrar suscripciones.</Typography>
          </Box>
          <Chip color={gatewayDraft.isActive ? "success" : "default"} label={gatewayDraft.isActive ? "Bold activo" : "Bold inactivo"} />
        </Stack>
        {loadingGateways ? (
          <PageLoadingState label="Cargando pasarelas" compact />
        ) : (
          <Grid container spacing={1.5} mt={0.25}>
            <Grid item xs={12} md={3}>
              <FormControlLabel control={<Switch checked={gatewayDraft.isActive} onChange={(event) => updateGatewayDraft("isActive", event.target.checked)} />} label="Bold habilitado" />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth label="Nombre" value={gatewayDraft.displayName} onChange={(event) => updateGatewayDraft("displayName", event.target.value)} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth label="Merchant ID" value={gatewayDraft.merchantId} onChange={(event) => updateGatewayDraft("merchantId", event.target.value)} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth label="Public key" value={gatewayDraft.publicKey} onChange={(event) => updateGatewayDraft("publicKey", event.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="password"
                label={gatewayDraft.apiKeyConfigured ? "API key configurada" : "API key"}
                value={gatewayDraft.apiKey}
                helperText={gatewayDraft.apiKeyConfigured ? "Deja en blanco para conservar la actual." : "Guarda la API key de Bold de LYPRO."}
                onChange={(event) => updateGatewayDraft("apiKey", event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="password"
                label={gatewayDraft.webhookSecretConfigured ? "Webhook secret configurado" : "Webhook secret"}
                value={gatewayDraft.webhookSecret}
                helperText={gatewayDraft.webhookSecretConfigured ? "Deja en blanco para conservar el actual." : "Usalo para validar firmas cuando Bold lo envie."}
                onChange={(event) => updateGatewayDraft("webhookSecret", event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <TextField
                fullWidth
                label="Webhook Bold"
                value={gatewayWebhookUrl}
                slotProps={{ input: { readOnly: true, endAdornment: renderCopyAdornment(gatewayWebhookUrl) } }}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField fullWidth label="Notas internas" value={gatewayDraft.notes} onChange={(event) => updateGatewayDraft("notes", event.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">
                Este webhook es de LYPRO, no de la compañía cliente. Las compañías solo ven su plan y sus datos de pago.
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" startIcon={<Save />} disabled={saving} onClick={() => void submitGateway()}>
                Guardar pasarela
              </Button>
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );

  const renderUsersTable = () => (
    <>
      <TableContainer sx={{ mt: 1, overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 1160 }}>
          <TableHead>
            <TableRow>
              {[
                ["fullName", "Usuario"],
                ["email", "Email"],
                ["company", "Compañía"],
                ["role", "Rol"],
                ["isActive", "Estado"],
                ["activity", "Última actividad"],
                ["integrations", "Integraciones"],
              ].map(([key, label]) => (
                <TableCell key={key} sortDirection={userSort.key === key ? userSort.direction : false}>
                  <TableSortLabel
                    active={userSort.key === key}
                    direction={userSort.key === key ? userSort.direction : "asc"}
                    onClick={() => toggleUserSort(key as typeof userSort.key)}
                  >
                    {label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedUsers.map((item) => (
              <TableRow key={item.userId} hover>
                <TableCell sx={{ fontWeight: 700 }}>{item.fullName}</TableCell>
                <TableCell>{item.email || "Sin email"}</TableCell>
                <TableCell>{resolveCompanyName(item.company)}</TableCell>
                <TableCell>{item.role}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    icon={item.isActive ? <CheckCircle /> : <Block />}
                    label={item.isActive ? "Activo" : "Deshabilitado"}
                    color={item.isActive ? "success" : "default"}
                    variant={item.isActive ? "filled" : "outlined"}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{formatUserActivity(item)}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {item.integrations.join(", ") || "Sin integraciones"}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" gap={1} justifyContent="flex-end">
                    <Button variant="outlined" size="small" startIcon={<LockReset />} onClick={() => setPasswordUser(item)}>Contraseña</Button>
                    <Button variant="outlined" size="small" startIcon={<Edit />} onClick={() => openUserEdit(item)}>Editar</Button>
                    <Button
                      color="error"
                      variant="outlined"
                      size="small"
                      startIcon={deletingId === `user:${item.userId}` ? <CircularProgress size={14} /> : <DeleteForever />}
                      disabled={Boolean(deletingId)}
                      onClick={() => void deleteUser(item)}
                    >
                      Eliminar
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!sortedUsers.length && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                    No hay usuarios registrados.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );

  return (
    <Box sx={dashboardPageSx}>
      <Box sx={{ ...dashboardPagePaddingSx, px: { xs: 1, sm: 2 }, maxWidth: "none" }}>
        <Stack spacing={1.25}>
          {/* <Box>
            <Typography variant="h4" fontWeight={700}>Lypro Admin</Typography>
            <Typography color="text.secondary" mt={0.5}>Administra compañías, accesos y aprovisionamiento operativo.</Typography>
          </Box> */}

          <SectionTabs
            value={String(tab)}
            ariaLabel="Secciones de Lypro Admin"
            options={[
              { value: "0", label: "Compañías", icon: <Business fontSize="small" /> },
              { value: "1", label: "Usuarios", icon: <People fontSize="small" /> },
              { value: "2", label: "Planes", icon: <CreditCard fontSize="small" /> },
              { value: "3", label: "Solicitudes Travel", icon: <ShoppingBag fontSize="small" /> },
            ]}
            onChange={(value) => {
              const nextValue = Number(value);
              setTab(nextValue);
              if (nextValue === 3) void loadTravelRequests();
            }}
          />

          {tab === 3 ? (
            <Card sx={dashboardCardSx}>
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} spacing={1}>
                    <Box><Typography variant="h6">Solicitudes de agencias</Typography><Typography variant="body2" color="text.secondary">Vista global de soporte. Los cambios quedan registrados.</Typography></Box>
                    <Button variant="outlined" onClick={() => void loadTravelRequests()}>Actualizar</Button>
                  </Stack>
                  {loadingTravelRequests ? <PageLoadingState label="Cargando solicitudes Travel" compact /> : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead><TableRow><TableCell>Referencia</TableCell><TableCell>Agencia / origen</TableCell><TableCell>Viajero</TableCell><TableCell>Oferta</TableCell><TableCell>Fecha</TableCell><TableCell>Estado</TableCell></TableRow></TableHead>
                        <TableBody>
                          {travelRequests.map((booking) => (
                            <TableRow key={booking.id}>
                              <TableCell>{booking.publicReference}</TableCell>
                              <TableCell><Stack spacing={0.25}><Typography variant="body2">{companyById.get(booking.companyId)?.name || booking.storeName || booking.companyId}</Typography><Typography variant="caption" color="text.secondary">{booking.storeDomain || "Sin dominio"}</Typography></Stack></TableCell>
                              <TableCell><Stack spacing={0.25}><Typography variant="body2">{booking.customerName}</Typography><Typography variant="caption" color="text.secondary">{booking.customerPhone}</Typography></Stack></TableCell>
                              <TableCell>{booking.packageName} · {booking.travelers} viajeros</TableCell>
                              <TableCell>{booking.startDate}</TableCell>
                              <TableCell><TextField select size="small" value={booking.status} onChange={(event) => void updateGlobalTravelStatus(booking, event.target.value as TravelBookingStatus)} sx={{ minWidth: 175 }}>{(["pending", "contacted", "quoted", "changes_requested", "confirmed", "cancelled"] as TravelBookingStatus[]).map((status) => <MenuItem key={status} value={status}>{({ pending: "Pendiente", contacted: "Contactado", quoted: "Cotizado", changes_requested: "Cambios solicitados", confirmed: "Confirmado", cancelled: "Cancelado" } as Record<TravelBookingStatus, string>)[status]}</MenuItem>)}</TextField></TableCell>
                            </TableRow>
                          ))}
                          {!travelRequests.length && <TableRow><TableCell colSpan={6} align="center">No hay solicitudes de Travel todavía.</TableCell></TableRow>}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Stack>
              </CardContent>
            </Card>
          ) : tab === 0 && loadingCompanies ? (
            <PageLoadingState label="Cargando compañías" compact />
          ) : tab === 0 ? (
            <Grid container spacing={0}>
              {false && (
              <Grid item xs={12}>
                <Card sx={dashboardCardSx}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Typography variant="h6" sx={dashboardSectionTitleSx}>Nueva compañía</Typography>
                    <Typography variant="body2" color="text.secondary" mt={0.5} mb={1.5}>Configúrala por pasos, igual que Integración Mapos.</Typography>
                    {renderCompanyTabs(companyTab, setCompanyTab, company.companyType)}
                    {renderCompanyFormContent({ value: company, onChange: updateCompany, activeTab: companyTab })}
                    <Button sx={{ mt: 2 }} variant="contained" startIcon={<Add />} disabled={saving} onClick={() => void submitCompany()}>
                      Crear compañía
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              )}

              <Card sx={{ ...dashboardCardSx, width: "100%" }}>
                  <CardContent sx={{ p: { xs: 1.25, sm: 1.5 } }}>
                    <Typography variant="h6" sx={dashboardSectionTitleSx}>Compañías existentes</Typography>
                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={1} mt={0.5}>
                      <Typography variant="body2" color="text.secondary">Administra compañías y crea nuevas desde el modal.</Typography>
                      <Button variant="contained" startIcon={<Add />} onClick={openCompanyCreate}>
                        Nueva compañía
                      </Button>
                    </Stack>
                    {renderCompaniesTable()}
                    <Stack spacing={0.75} mt={1} sx={{ display: "none" }}>
                      {companies.map((item) => (
                        <Box key={item.companyId} sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1 }}>
                          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={1.25}>
                            <Box>
                              <Typography fontWeight={700}>{item.name || item.companyId}</Typography>
                              <Typography variant="body2" color="text.secondary">{item.companyId} · {getAgentWorkflowLabel(item.agentWorkflow)}</Typography>
                              <Stack direction="row" gap={0.4} flexWrap="wrap" mt={0.75}>
                                {item.indexProvisioning?.length ? (
                                  item.indexProvisioning.map((index, indexPosition) => (
                                    <Chip
                                      size="small"
                                      key={`${index.collectionId}-${indexPosition}`}
                                      label={`${index.collectionId}: ${index.state}`}
                                      color={index.state === "ERROR" ? "error" : index.state === "READY" ? "success" : "warning"}
                                      title={index.error || index.operation || ""}
                                      sx={indexChipSx}
                                    />
                                  ))
                                ) : (
                                  <Chip size="small" label="Sin índices reportados" variant="outlined" sx={indexChipSx} />
                                )}
                              </Stack>
                            </Box>
                            <Stack direction="row" gap={1} alignItems="center">
                              <Button variant="outlined" size="small" startIcon={<Edit />} onClick={() => openCompanyEdit(item)}>
                                Editar
                              </Button>
                            </Stack>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
            </Grid>
          ) : tab === 2 && loadingPlans ? (
            <PageLoadingState label="Cargando planes" compact />
          ) : tab === 2 ? (
            <Grid container spacing={0}>
              {renderPaymentGatewayCard()}
              <Card sx={{ ...dashboardCardSx, width: "100%" }}>
                <CardContent sx={{ p: { xs: 1.25, sm: 1.5 } }}>
                  <Typography variant="h6" sx={dashboardSectionTitleSx}>Planes por integración</Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={1} mt={0.5}>
                    <Typography variant="body2" color="text.secondary">Define precios por integración, moneda y frecuencia para asignarlos a compañías.</Typography>
                    <Button variant="contained" startIcon={<Add />} onClick={openPlanCreate}>
                      Nuevo plan
                    </Button>
                  </Stack>
                  {renderPlansTable()}
                </CardContent>
              </Card>
            </Grid>
          ) : loadingUsers ? (
            <PageLoadingState label="Cargando usuarios" compact />
          ) : (
            <Grid container spacing={0}>
              {false && (
              <Grid item xs={12} md={5}>
                <Card sx={dashboardCardSx}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Typography variant="h6" sx={dashboardSectionTitleSx}>Nuevo usuario</Typography>
                    <Grid container spacing={1.5} mt={0.5}>
                      <Grid item xs={12}><TextField required fullWidth label="Nombre completo" value={user.fullName} onChange={(event) => setUser({ ...user, fullName: event.target.value })} /></Grid>
                      <Grid item xs={12}><TextField required fullWidth type="email" label="Email" value={user.email} onChange={(event) => setUser({ ...user, email: event.target.value })} /></Grid>
                      <Grid item xs={12}><TextField required fullWidth type="password" label="Contraseña temporal" value={user.temporaryPassword} onChange={(event) => setUser({ ...user, temporaryPassword: event.target.value })} /></Grid>
                      <Grid item xs={12} md={7}>
                        <TextField select required fullWidth label="Compañía" value={user.company} onChange={(event) => setUser({ ...user, company: event.target.value, integrations: getDefaultIntegrationsForCompany(event.target.value) })}>
                          {companies.map((item) => <MenuItem key={item.companyId} value={item.companyId}>{item.name} ({item.companyId})</MenuItem>)}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={5}><TextField fullWidth label="Rol" value={user.role} onChange={(event) => setUser({ ...user, role: event.target.value })} /></Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" fontWeight={700}>Integraciones</Typography>
                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                          {getAllowedIntegrationsForCompany(user.company).map((integration) => (
                            <FormControlLabel key={integration} control={<Checkbox checked={user.integrations.includes(integration)} onChange={(event) => setUser({ ...user, integrations: event.target.checked ? [...user.integrations, integration] : user.integrations.filter((item) => item !== integration) })} />} label={getIntegrationLabel(integration)} />
                          ))}
                        </Stack>
                      </Grid>
                    </Grid>
                    <Button sx={{ mt: 2 }} variant="contained" startIcon={<Add />} disabled={saving} onClick={() => void submitUser()}>Crear usuario</Button>
                  </CardContent>
                </Card>
              </Grid>
              )}

              <Card sx={{ ...dashboardCardSx, width: "100%" }}>
                  <CardContent sx={{ p: { xs: 1.25, sm: 1.5 } }}>
                    <Typography variant="h6" sx={dashboardSectionTitleSx}>Usuarios existentes</Typography>
                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={1} mt={0.5}>
                      <Typography variant="body2" color="text.secondary">Administra usuarios y crea nuevos desde el modal.</Typography>
                      <Button variant="contained" startIcon={<Add />} onClick={openUserCreate}>
                        Nuevo usuario
                      </Button>
                    </Stack>
                    {renderUsersTable()}
                    <Stack spacing={0.75} mt={1} sx={{ display: "none" }}>
                      {users.map((item) => (
                        <Box key={item.userId} sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1 }}>
                          <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" gap={1}>
                            <Box>
                              <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                                <Typography fontWeight={700}>{item.fullName}</Typography>
                                <Chip size="small" icon={item.isActive ? <CheckCircle /> : <Block />} label={item.isActive ? "Activo" : "Deshabilitado"} color={item.isActive ? "success" : "default"} variant={item.isActive ? "filled" : "outlined"} />
                              </Stack>
                              <Typography variant="body2" color="text.secondary">{item.email} · {companyById.get(item.company)?.name || item.company} · {item.role}</Typography>
                              <Typography variant="caption" color="text.secondary">{item.integrations.join(", ") || "Sin integraciones"}</Typography>
                            </Box>
                            <Stack direction="row" gap={1}>
                              <Button variant="outlined" size="small" startIcon={<LockReset />} onClick={() => setPasswordUser(item)}>Contraseña</Button>
                              <Button variant="outlined" size="small" startIcon={<Edit />} onClick={() => openUserEdit(item)}>Editar</Button>
                            </Stack>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
            </Grid>
          )}
        </Stack>
      </Box>

      <Dialog open={creatingCompany} onClose={closeCompanyCreate} maxWidth="lg" fullWidth>
        <DialogTitle>Nueva compañía</DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            Configúrala por pasos, igual que Integración Mapos.
          </Typography>
          {renderCompanyTabs(companyTab, setCompanyTab, company.companyType)}
          {renderCompanyFormContent({
            value: company,
            onChange: updateCompany,
            activeTab: companyTab,
          })}
        </DialogContent>
        <DialogActions>
          <Button startIcon={<Close />} onClick={closeCompanyCreate} disabled={saving}>Cancelar</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => void submitCompany()} disabled={saving}>Crear compañía</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={creatingPlan} onClose={() => { setCreatingPlan(false); setEditingPlan(null); }} maxWidth="md" fullWidth>
        <DialogTitle>{editingPlan ? "Editar plan" : "Nuevo plan"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={1.5} mt={0.5}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth required disabled={Boolean(editingPlan)} label="ID del plan" value={planDraft.planId} onChange={(event) => setPlanDraft({ ...planDraft, planId: event.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth required label="Nombre" value={planDraft.name} onChange={(event) => setPlanDraft({ ...planDraft, name: event.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField select fullWidth required label="Integración" value={planDraft.integrationCode} onChange={(event) => setPlanDraft({ ...planDraft, integrationCode: event.target.value })}>
                {INTEGRATION_OPTIONS.map((integration) => (
                  <MenuItem key={integration} value={integration}>{integration}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField select fullWidth required label="Moneda" value={planDraft.currency} onChange={(event) => setPlanDraft({ ...planDraft, currency: event.target.value })}>
                {CURRENCY_OPTIONS.map((currency) => (
                  <MenuItem key={currency} value={currency}>{currency}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth required type="number" label="Precio" value={planDraft.amount} onChange={(event) => setPlanDraft({ ...planDraft, amount: Number(event.target.value) })} slotProps={{ htmlInput: { min: 0, step: 1 } }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField select fullWidth label="Frecuencia" value={planDraft.interval} onChange={(event) => setPlanDraft({ ...planDraft, interval: event.target.value as AdminBillingPlanInput["interval"] })}>
                {BILLING_INTERVAL_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel control={<Switch checked={planDraft.isActive !== false} onChange={(event) => setPlanDraft({ ...planDraft, isActive: event.target.checked })} />} label={planDraft.isActive !== false ? "Plan activo" : "Plan inactivo"} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={2} label="Descripción" value={planDraft.description} onChange={(event) => setPlanDraft({ ...planDraft, description: event.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Features separadas por coma"
                value={(planDraft.features || []).join(", ")}
                onChange={(event) => setPlanDraft({
                  ...planDraft,
                  features: event.target.value.split(",").map((item) => item.trim()).filter(Boolean),
                })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<Close />} onClick={() => { setCreatingPlan(false); setEditingPlan(null); }} disabled={saving}>Cancelar</Button>
          <Button variant="contained" startIcon={<Save />} onClick={() => void submitPlan()} disabled={saving}>Guardar plan</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={creatingUser} onClose={closeUserCreate} maxWidth="md" fullWidth>
        <DialogTitle>Nuevo usuario</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={1.5} mt={0.5}>
            <Grid item xs={12}><TextField required fullWidth label="Nombre completo" value={user.fullName} onChange={(event) => setUser({ ...user, fullName: event.target.value })} /></Grid>
            <Grid item xs={12}><TextField required fullWidth type="email" label="Email" value={user.email} onChange={(event) => setUser({ ...user, email: event.target.value })} /></Grid>
            <Grid item xs={12}><TextField required fullWidth type="password" label="Contraseña temporal" value={user.temporaryPassword} onChange={(event) => setUser({ ...user, temporaryPassword: event.target.value })} /></Grid>
            <Grid item xs={12} md={7}>
              <TextField select required fullWidth label="Compañía" value={user.company} onChange={(event) => setUser({ ...user, company: event.target.value, integrations: getDefaultIntegrationsForCompany(event.target.value) })}>
                {companies.map((item) => <MenuItem key={item.companyId} value={item.companyId}>{item.name} ({item.companyId})</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={5}><TextField fullWidth label="Rol" value={user.role} onChange={(event) => setUser({ ...user, role: event.target.value })} /></Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight={700}>Integraciones</Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.5}>
                {getAllowedIntegrationsForCompany(user.company).map((integration) => (
                  <FormControlLabel key={integration} control={<Checkbox checked={user.integrations.includes(integration)} onChange={(event) => setUser({ ...user, integrations: event.target.checked ? [...user.integrations, integration] : user.integrations.filter((item) => item !== integration) })} />} label={getIntegrationLabel(integration)} />
                ))}
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<Close />} onClick={closeUserCreate} disabled={saving}>Cancelar</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => void submitUser()} disabled={saving}>Crear usuario</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editingCompany)} onClose={() => setEditingCompany(null)} maxWidth="lg" fullWidth>
        <DialogTitle>Editar compañía</DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 1.5, sm: 2 } }}>
          {renderCompanyTabs(editCompanyTab, setEditCompanyTab, companyDraft.companyType)}
          {renderCompanyFormContent({
            value: companyDraft,
            onChange: updateCompanyDraft,
            activeTab: editCompanyTab,
            disabledCompanyId: true,
          })}
        </DialogContent>
        <DialogActions>
          <Button startIcon={<Close />} onClick={() => setEditingCompany(null)} disabled={saving}>Cancelar</Button>
          {editCompanyTab === 4 && (
            <Button
              variant="outlined"
              startIcon={validatingCompanyWhatsapp ? <CircularProgress size={16} /> : <WhatsApp />}
              onClick={() => void saveAndValidateCompanyWhatsapp()}
              disabled={saving || validatingCompanyWhatsapp}
            >
              {validatingCompanyWhatsapp ? "Validando con Meta…" : "Guardar y validar Meta"}
            </Button>
          )}
          <Button variant="contained" startIcon={saving ? <CircularProgress size={16} /> : <Save />} onClick={() => void submitCompanyEdit()} disabled={saving}>Guardar cambios</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editingUser)} onClose={() => setEditingUser(null)} maxWidth="md" fullWidth>
        <DialogTitle>Editar usuario</DialogTitle>
        <DialogContent dividers>
          {userDraft && (
            <Stack spacing={2}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={6}><TextField fullWidth required label="Nombre completo" value={userDraft.fullName} onChange={(event) => setUserDraft({ ...userDraft, fullName: event.target.value })} /></Grid>
                <Grid item xs={12} md={6}><TextField fullWidth disabled label="Email" value={editingUser?.email || ""} /></Grid>
                <Grid item xs={12} md={6}>
                  <TextField select required fullWidth label="Compañía" value={userDraft.company} onChange={(event) => setUserDraft({ ...userDraft, company: event.target.value, integrations: getDefaultIntegrationsForCompany(event.target.value) })}>
                    {companies.map((item) => <MenuItem key={item.companyId} value={item.companyId}>{item.name} ({item.companyId})</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}><TextField fullWidth required label="Rol" value={userDraft.role} onChange={(event) => setUserDraft({ ...userDraft, role: event.target.value })} /></Grid>
              </Grid>
              <Divider />
              <FormControlLabel control={<Switch checked={userDraft.isActive} onChange={(event) => setUserDraft({ ...userDraft, isActive: event.target.checked })} />} label={userDraft.isActive ? "Usuario activo" : "Usuario deshabilitado"} />
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>Integraciones</Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {getAllowedIntegrationsForCompany(userDraft.company).map((integration) => (
                    <FormControlLabel key={integration} control={<Checkbox checked={userDraft.integrations.includes(integration)} onChange={(event) => setUserDraft({ ...userDraft, integrations: event.target.checked ? [...userDraft.integrations, integration] : userDraft.integrations.filter((item) => item !== integration) })} />} label={getIntegrationLabel(integration)} />
                  ))}
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button startIcon={<Close />} onClick={() => setEditingUser(null)} disabled={saving}>Cancelar</Button>
          <Button variant="contained" startIcon={<Save />} onClick={() => void submitUserEdit()} disabled={saving}>Guardar cambios</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(passwordUser)} onClose={() => setPasswordUser(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Cambiar contraseña</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" mb={2}>Asigna una nueva contraseña para {passwordUser?.email}.</Typography>
          <TextField autoFocus fullWidth type="password" label="Nueva contraseña" value={newPassword} helperText="Mínimo 6 caracteres." onChange={(event) => setNewPassword(event.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button startIcon={<Close />} onClick={() => { setPasswordUser(null); setNewPassword(""); }} disabled={saving}>Cancelar</Button>
          <Button variant="contained" startIcon={<LockReset />} onClick={() => void submitPassword()} disabled={saving}>Actualizar contraseña</Button>
        </DialogActions>
      </Dialog>

      <FeedbackSnackbar
        open={Boolean(error || success)}
        message={error || success}
        severity={error ? "error" : "success"}
        onClose={() => {
          setError("");
          setSuccess("");
        }}
      />
    </Box>
  );
};

export default AdminPage;
